import semver from 'semver';
import { z } from 'zod';

import { PAYMENT_METHOD, REQ_SOURCE } from '../../constants';
import { isEmpty } from '../../utils';
import { createGetExtraDataParsedHelper } from '../nfctron-global';

/* -------------------------------------------------------------------------------------------------
 * Ticket Type
 * -----------------------------------------------------------------------------------------------*/

/**
 * Schema for ticket type extra data without default values
 * Defines all possible configuration options for ticket types
 */
export const ticketTypeExtraDataWithoutDefaultsSchema = z.object({
	/** original ticket price before any discounts */
	originalPrice: z.number().optional().describe('Original price of the ticket before any discounts'),
	/** whether to display the original price */
	showOriginalPrice: z.boolean().optional().describe('Controls visibility of the original price'),
	/** whether to display the difference between original and current price */
	showOriginalPriceDifference: z.boolean().optional().describe('Controls visibility of the price difference'),
	/** default quantity when adding to cart */
	addToCartQty: z.number().optional().describe('Default quantity when adding ticket to cart'),
	/** promotion campaign configuration */
	promotionCampaign: z
		.object({
			type: z.literal('mc-click-to-pay').describe('Type of promotion campaign'),
			allowedPaymentMethods: z.array(z.nativeEnum(PAYMENT_METHOD)).optional().describe('Payment methods allowed for this promotion'),
		})
		.optional()
		.describe('Configuration for promotion campaigns like ClickToPay'),
	/** automatically append these ticket types when this ticket is added to cart */
	autoAppendTicketTypes: z
		.array(
			z.object({
				ticketTypeId: z.string().describe('ID of the ticket type to append'),
				quantity: z.number().describe('Quantity of tickets to append'),
			}),
		)
		.optional()
		.describe('Ticket types to automatically add when this ticket is added to cart'),
	/** additional features to display for this ticket type */
	additionalFeatures: z
		.record(
			z.object({
				label: z.string().describe('Label of the feature'),
				icon: z.string().optional().describe('Icon identifier for the feature'),
				tooltip: z.string().optional().describe('Tooltip text for the feature'),
				sheet: z
					.object({
						title: z.string().describe('Title of the feature detail sheet'),
						descriptionMd: z.string().describe('Markdown description for the feature detail sheet'),
					})
					.describe('Detailed information shown in a sheet'),
			}),
		)
		.optional()
		.describe('Additional features to display for this ticket type'),
	/** sources where this ticket type is not supported */
	unsupportedSources: z
		.array(
			z.object({
				reqSourceId: z.nativeEnum(REQ_SOURCE).describe('Request source identifier'),
				versionMatcher: z
					.string()
					.refine((v) => semver.valid(v) !== null, 'Must be a valid semver version')
					.describe('Minimum version that is unsupported'),
				versionMatcherTo: z
					.string()
					.optional()
					.refine((v) => {
						if (isEmpty(v)) return true;
						return semver.valid(v) !== null;
					}, 'Must be a valid semver version')
					.describe('Maximum version that is unsupported (inclusive)'),
				semverString: z.string().describe('Semver range string for version comparison'),
			}),
		)
		.optional()
		.describe('Configuration for sources where this ticket type should not be available'),
	/** temporary flag to hide on mobile devices */
	hideMobile: z.boolean().optional().describe('Whether to hide this ticket type on mobile devices (temporary solution)'),
});
export const ticketTypeExtraDataWithoutDefaultsEmpty = ticketTypeExtraDataWithoutDefaultsSchema.parse(
	{} satisfies z.input<typeof ticketTypeExtraDataWithoutDefaultsSchema>,
);

/**
 * Schema for ticket type extra data with default values
 * Extends the base schema with default values for required fields
 */
export const ticketTypeExtraDataWithDefaultsSchema = ticketTypeExtraDataWithoutDefaultsSchema.merge(
	z.object({
		showOriginalPrice: ticketTypeExtraDataWithoutDefaultsSchema.shape.showOriginalPrice.default(true),
		showOriginalPriceDifference: ticketTypeExtraDataWithoutDefaultsSchema.shape.showOriginalPriceDifference.default(true),
		addToCartQty: ticketTypeExtraDataWithoutDefaultsSchema.shape.addToCartQty.default(1),
		autoAppendTicketTypes: ticketTypeExtraDataWithoutDefaultsSchema.shape.autoAppendTicketTypes.default([]),
		additionalFeatures: ticketTypeExtraDataWithoutDefaultsSchema.shape.additionalFeatures.default({}),
		unsupportedSources: ticketTypeExtraDataWithoutDefaultsSchema.shape.unsupportedSources.default([]),
		hideMobile: ticketTypeExtraDataWithoutDefaultsSchema.shape.hideMobile.default(false),
	}),
);
export const ticketTypeExtraDataWithDefaultsEmpty = ticketTypeExtraDataWithDefaultsSchema.parse(
	{} satisfies z.input<typeof ticketTypeExtraDataWithDefaultsSchema>,
);

/**
 * Helper for parsing ticket type extra data
 * Handles both versions with and without defaults
 */
export const getTicketTypeExtraDataParsed = createGetExtraDataParsedHelper({
	schemaWithDefaults: ticketTypeExtraDataWithDefaultsSchema,
	schemaWithoutDefaults: ticketTypeExtraDataWithoutDefaultsSchema,
	defaultData: ticketTypeExtraDataWithDefaultsEmpty,
});

/* -------------------------------------------------------------------------------------------------
 * Ticket Type Group
 * -----------------------------------------------------------------------------------------------*/

/**
 * Schema for ticket type group extra data without default values
 * Defines all possible configuration options for ticket type groups
 */
export const ticketTypeGroupExtraDataWithoutDefaultsSchema = z.object({
	/** background color of the ticket type group */
	color: z.string().optional().describe('Background color of the ticket type group'),
});
export const ticketTypeGroupExtraDataWithoutDefaultsEmpty = ticketTypeGroupExtraDataWithoutDefaultsSchema.parse(
	{} satisfies z.input<typeof ticketTypeGroupExtraDataWithoutDefaultsSchema>,
);
export const ticketTypeGroupExtraDataWithDefaultsSchema = ticketTypeGroupExtraDataWithoutDefaultsSchema;
export const ticketTypeGroupExtraDataWithDefaultsEmpty = ticketTypeGroupExtraDataWithDefaultsSchema.parse(
	{} satisfies z.input<typeof ticketTypeGroupExtraDataWithDefaultsSchema>,
);

/**
 * Helper for parsing ticket type group extra data
 * Handles both versions with and without defaults
 */
export const getTicketTypeGroupExtraDataParsed = createGetExtraDataParsedHelper({
	schemaWithDefaults: ticketTypeGroupExtraDataWithDefaultsSchema,
	schemaWithoutDefaults: ticketTypeGroupExtraDataWithoutDefaultsSchema,
	defaultData: ticketTypeGroupExtraDataWithDefaultsEmpty,
});
